<template>
  <div class="sf-el multi-choice-input">
    <div class="sf-multi-choice">
      <label class="sf-el__label">
        {{ label }}
        <span v-if="required" class="sf-el__required">*</span>
      </label>
      <p v-if="caption" class="sf-el__caption">{{ caption }}</p>
      <div v-if="add_search_bar" class="sf-search-bar">
        <input
          type="text"
          v-model="searchQuery"
          :placeholder="dummy ? 'Search...' : $t('form.fields.choice.search_placeholder')"
          class="sf-search-input"
        />
      </div>
      <div
        class="sf-multi-choice__grid"
        :class="{ [`sf-multi-choice__grid--display-${display}`]: display }"
      >
        <div
          v-for="(choice, index) in filteredChoices"
          :key="index"
          class="choice"
          :class="{ 'is-selected': selectedChoices.includes(choice.id) }"
        >
          <input
            type="checkbox"
            :name="name"
            :value="choice.id"
            v-model="selectedChoices"
            class="checkbox-button"
            :id="`${choice.id}-choice-${index}`"
          />
          <label :for="`${choice.id}-choice-${index}`">
            <img :src="choice.image" class="choice__img" alt="Choice Image" v-if="choice.image" />
            <div class="choice__label">
              <div class="choice__checkbox">
                <BaseIcon name="check" size="16" />
              </div>
              <div class="choice__label-content">
                <span class="choice__title">
                  {{ choice.content }}
                </span>
                <span class="choice__description" v-if="choice.description">
                  {{ choice.description }}
                </span>
              </div>
            </div>
          </label>
        </div>
      </div>
      <div v-if="shouldShowMoreButton && !dummy" class="sf-show-more">
        <button type="button" @click="showAllChoices = true" class="sf-show-more-btn">
          {{ $t('form.fields.choice.show_more', { count: remainingChoicesCount }) }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
  import BaseIcon from '../../BaseIcon.vue';
  import { watch, ref, computed } from 'vue';
  export default {
    name: 'MultiChoiceElement',
    components: {
      BaseIcon,
    },
    props: {
      label: String,
      description: String,
      caption: String,
      choices: Array,
      name: String,
      modelValue: String,
      display: String,
      required: Boolean,
      add_search_bar: Boolean,
      dummy: { type: Boolean, default: false },
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
      const selectedChoices = ref(props.modelValue || []);
      const searchQuery = ref('');
      const showAllChoices = ref(false);

      // Find the initial selected choice by content
      const findChoiceById = (id) => {
        return props.choices.find((choice) => choice.id === id);
      };

      const filteredChoices = computed(() => {
        if (!props.add_search_bar) {
          // No search bar - show all choices
          return props.choices || [];
        }

        if (!searchQuery.value.trim()) {
          // Search bar enabled but no query
          if (showAllChoices.value) {
            // Show all choices if "show more" was clicked
            return props.choices || [];
          } else {
            // Show maximum 12 choices by default
            return props.choices ? props.choices.slice(0, 12) : [];
          }
        }

        const query = searchQuery.value.toLowerCase().trim();
        const filtered = props.choices
          ? props.choices.filter(
              (choice) =>
                choice.content.toLowerCase().includes(query) ||
                (choice.description && choice.description.toLowerCase().includes(query))
            )
          : [];

        // Show maximum 12 filtered results
        return filtered.slice(0, 12);
      });

      const shouldShowMoreButton = computed(() => {
        if (!props.add_search_bar || showAllChoices.value || searchQuery.value.trim()) {
          return false;
        }
        const totalChoices = props.choices ? props.choices.length : 0;
        return totalChoices > 12;
      });

      const remainingChoicesCount = computed(() => {
        const totalChoices = props.choices ? props.choices.length : 0;
        return Math.max(0, totalChoices - 12);
      });

      watch(selectedChoices, (newValues) => {
        emit('update:modelValue', newValues);
      });

      return {
        selectedChoices,
        searchQuery,
        filteredChoices,
        showAllChoices,
        shouldShowMoreButton,
        remainingChoicesCount,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@shared/assets/css/base/mixins.scss';
  @import './styles/choices.scss';

  .sf-search-bar {
    margin-bottom: 1rem;
  }

  .sf-show-more {
    margin-top: 1rem;
    text-align: center;
  }

  .sf-show-more-btn {
    background: none;
    border: none;
    color: var(--cs-text-body);
    cursor: pointer;
    font-size: 0.875rem;
    text-decoration: underline;
    padding: 0.5rem;

    &:hover {
      color: var(--cs-button-default-background);
    }

    &:focus {
      outline: none;
      color: var(--cs-button-default-background);
    }
  }

  .choice {
    &__checkbox {
      border-radius: var(--cs-checkbox-border-radius);
    }
  }
</style>
